interface HoverBoxMapConfig {
  hoverBoxExitDelay: number;
  hoverBoxXOffset: number;
  hoverBoxYOffset: number;
}

export class HoverBoxMap {
  private elMap: HTMLElement;
  private elTargets: Element[];
  private config: HoverBoxMapConfig;
  private elHoverBox: HTMLElement | undefined;
  private exitDelay: number | undefined;

  /**
   *
   */
  public constructor(elMap: HTMLElement, elTargets: Element[], { hoverBoxExitDelay = 400, hoverBoxXOffset = 0, hoverBoxYOffset = 30 } = {}) {
    this.elMap = elMap;
    this.elTargets = elTargets;
    this.config = {
      hoverBoxExitDelay,
      hoverBoxXOffset,
      hoverBoxYOffset
    };

    this.createHoverBox();
    this.bindEvents();
  }

  /**
   *
   */
  private createHoverBox(): void {
    // if the map's positioning type hasn't been set, default to 'relative'
    this.elMap.style.position ||= 'relative';

    // create the hover content element and append it to the map
    const elHoverBox = document.createElement('aside');
    elHoverBox.classList.add('hover-box');
    elHoverBox.setAttribute('aria-live', 'polite');
    elHoverBox.setAttribute('aria-hidden', 'true');
    elHoverBox.style.position = 'absolute';
    elHoverBox.style.top = '0';
    elHoverBox.style.left = '0';
    this.elMap.append(elHoverBox);

    // keep a reference to the new element
    this.elHoverBox = elHoverBox;
  }

  /**
   *
   */
  private bindEvents(): void {
    // define interaction events for the map target areas
    for (const elTarget of this.elTargets) {
      elTarget.addEventListener('mouseenter', this.showHoverBox.bind(this));
      elTarget.addEventListener('focus', this.showHoverBox.bind(this));
      elTarget.addEventListener('mouseleave', this.hideHoverBox.bind(this));
      elTarget.addEventListener('blur', this.hideHoverBox.bind(this));
    }

    // define interaction events for the hover box
    this.elHoverBox?.addEventListener('mouseenter', this.cancelExitDelay.bind(this));
    this.elHoverBox?.addEventListener('focus', this.cancelExitDelay.bind(this));
    this.elHoverBox?.addEventListener('mouseleave', this.hideHoverBox.bind(this));
    this.elHoverBox?.addEventListener('blur', this.hideHoverBox.bind(this));
  }

  /**
   *
   */
  private cancelExitDelay(): void {
    if (this.exitDelay) {
      clearTimeout(this.exitDelay);
      this.exitDelay = undefined;
    }
  }

  /**
   *
   */
  public showHoverBox(event: Event): void {
    if (!this.elHoverBox) {
      return;
    }

    const elTarget = event.currentTarget;
    if (!(elTarget instanceof Element)) {
      return;
    }

    // cancel the exit delay timeout
    this.cancelExitDelay();

    // get target element's offsets relative to the map
    const offset = {
      left: elTarget.getBoundingClientRect().left - this.elMap.getBoundingClientRect().left,
      top: elTarget.getBoundingClientRect().top - this.elMap.getBoundingClientRect().top
    };

    // set the content of the hover box
    const elTemplate = elTarget.querySelector('.hover-map__template');
    const content = elTemplate instanceof HTMLTemplateElement
      ? elTemplate.content.cloneNode(true)
      : document.createElement('div');
    this.elHoverBox.replaceChildren(content);
    // set the position of the hover box
    this.elHoverBox.style.left = (offset.left + this.config.hoverBoxXOffset) + 'px';
    this.elHoverBox.style.top = (offset.top + this.config.hoverBoxYOffset) + 'px';
    // flag the hover box as visible
    this.elHoverBox.setAttribute('aria-hidden', 'false');
  }

  /**
   *
   */
  public hideHoverBox(): void {
    // delay for {config.hoverBoxExitDelay}ms before hiding the hover box
    this.exitDelay = window.setTimeout(() => {
      this.elHoverBox?.setAttribute('aria-hidden', 'true');
    }, this.config.hoverBoxExitDelay);
  }
}

(() => {
  const onReady = () => {
    // create image map functionality
    for (const elHoverMap of [...document.querySelectorAll('.hover-map')].filter(el => el instanceof HTMLElement)) {
      const targets = [...elHoverMap.querySelectorAll('.hover-map__target')].filter(el => el instanceof Element);
      new HoverBoxMap(elHoverMap, targets);
    }
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', onReady) : onReady();
})();
